import { z } from "zod";

// 施策: one thing the office does to win more customers from search — a new
// article, a rewrite, an FAQ, structured data… — carried through the
// marketing PDCA: proposed, approved, done, measured, finished.

export const INITIATIVE_KINDS = ["article", "rewrite", "faq", "structured_data", "profile", "sns", "map", "campaign", "store", "other"] as const;
export type InitiativeKind = (typeof INITIATIVE_KINDS)[number];
export const INITIATIVE_KIND_LABELS: Record<InitiativeKind, string> = {
  article: "記事の新規作成",
  rewrite: "記事のリライト",
  faq: "FAQ の追加",
  structured_data: "構造化データ",
  profile: "事務所・監修者情報の整備",
  sns: "SNS の投稿",
  map: "地図（Google ビジネスプロフィール）の整備",
  campaign: "キャンペーン・クーポン",
  store: "店内の案内・接客",
  other: "その他",
};

/** The kinds that make sense for an office that writes articles, and for a 店舗. */
export const INITIATIVE_KINDS_BY_FEATURE: { articles: readonly InitiativeKind[]; store: readonly InitiativeKind[] } = {
  articles: ["article", "rewrite", "faq", "structured_data", "profile", "sns", "map", "campaign", "other"],
  store: ["sns", "map", "campaign", "store", "profile", "faq", "other"],
};

/** What the initiative is meant to move. */
export const INITIATIVE_GOALS = ["rank", "clicks", "inquiries", "ai_mentions", "visits", "map"] as const;
export type InitiativeGoal = (typeof INITIATIVE_GOALS)[number];
export const INITIATIVE_GOAL_LABELS: Record<InitiativeGoal, string> = {
  rank: "検索順位",
  clicks: "検索からのクリック",
  inquiries: "問い合わせ",
  ai_mentions: "AI の回答での紹介",
  visits: "サイトへの訪問",
  map: "地図からの表示・経路",
};

export const INITIATIVE_STATUSES = ["proposed", "approved", "in_progress", "measuring", "done", "dropped"] as const;
export type InitiativeStatus = (typeof INITIATIVE_STATUSES)[number];
export const INITIATIVE_STATUS_LABELS: Record<InitiativeStatus, string> = {
  proposed: "案",
  approved: "承認済み",
  in_progress: "実行中",
  measuring: "効果測定中",
  done: "完了",
  dropped: "見送り",
};

/** The steps an initiative may take from each status (the PDCA order, plus dropping and taking back). */
export const INITIATIVE_TRANSITIONS: Record<InitiativeStatus, readonly InitiativeStatus[]> = {
  proposed: ["approved", "dropped"],
  approved: ["in_progress", "proposed", "dropped"],
  in_progress: ["measuring", "approved", "dropped"],
  measuring: ["done", "in_progress"],
  done: ["measuring"],
  dropped: ["proposed"],
};

/** The label of the button that moves an initiative to `to`. */
export const INITIATIVE_ACTION_LABELS: Record<InitiativeStatus, string> = {
  proposed: "案に戻す",
  approved: "承認する",
  in_progress: "着手する",
  measuring: "効果測定に入る",
  done: "完了にする",
  dropped: "見送る",
};

export const canMoveInitiative = (from: InitiativeStatus, to: InitiativeStatus) => INITIATIVE_TRANSITIONS[from].includes(to);

/** Initiatives that can still be deleted: nothing has been done under them yet. */
export const isInitiativeDeletable = (status: InitiativeStatus) => status === "proposed" || status === "dropped";

export const InitiativeInput = z.object({
  kind: z.enum(INITIATIVE_KINDS, { message: "種類を選んでください" }),
  title: z.string().trim().min(1, "施策の名前を入力してください").max(100, "施策の名前は100文字以内にしてください"),
  /** Why: the problem it answers and what should change. */
  reason: z.string().trim().max(1000, "ねらいは1000文字以内にしてください").default(""),
  goal: z.enum(INITIATIVE_GOALS, { message: "目標の指標を選んでください" }),
  articleId: z.string().uuid("記事の指定が正しくありません").nullish(),
  /** The month it is planned for, as YYYY-MM. */
  dueMonth: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "実施する月の形式が正しくありません")
    .nullish(),
});
export type InitiativeInput = z.infer<typeof InitiativeInput>;

export const InitiativeTransitionInput = z.object({ to: z.enum(INITIATIVE_STATUSES) });
